import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  User,
  Mail,
  Phone,
  Building2,
  Calendar,
  Edit,
  Save,
  X,
  ChevronRight,
  ChevronDown,
  FileText,
  Users,
  Presentation,
  Settings,
  Star,
  Download,
  ExternalLink,
  Copy,
  Image,
  Video,
  Link,
  Globe,
  MessageSquare,
  Lock,
  Unlock
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useToast } from '../ui/use-toast';

export default function CompanyDetailModal({ open, onClose, company }) {
  const [taskDetails, setTaskDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [adminNotes, setAdminNotes] = useState('');
  const [togglingLock, setTogglingLock] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && company) {
      fetchTaskDetails();
    }
  }, [open, company]);

  const fetchTaskDetails = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCompanyTaskDetails(company._id);
      setTaskDetails(response.data);
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch task details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleNetworkPassesLock = async () => {
    setTogglingLock(true);
    try {
      const response = await adminAPI.toggleNetworkPassesLock(company._id);
      toast({
        title: 'Success',
        description: response.data.message,
      });
      // Update local company data if needed
      if (company) {
        company.networkingPassesLocked = response.data.networkingPassesLocked;
      }
      // Refresh task details to show updated lock status
      fetchTaskDetails();
    } catch (error) {
      console.error('Error toggling network passes lock:', error);
      toast({
        title: 'Error',
        description: 'Failed to toggle network passes lock',
        variant: 'destructive',
      });
    } finally {
      setTogglingLock(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await adminAPI.updateTaskStatus(taskId, newStatus, adminNotes);
      toast({
        title: 'Success',
        description: 'Task status updated successfully',
      });
      setEditingTask(null);
      setAdminNotes('');
      fetchTaskDetails();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task status',
        variant: 'destructive',
      });
    }
  };

  const toggleTaskExpansion = (taskId) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  const handleDownload = (url, filename) => {
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'Text copied to clipboard',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'overdue':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { variant: 'success', label: 'Completed' },
      in_progress: { variant: 'default', label: 'In Progress' },
      pending: { variant: 'secondary', label: 'Pending' },
      overdue: { variant: 'destructive', label: 'Overdue' },
      blocked: { variant: 'warning', label: 'Blocked' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTaskIcon = (taskType) => {
    const icons = {
      'company_details': <Building2 className="h-4 w-4" />,
      'networking_passes': <Users className="h-4 w-4" />,
      'presentation_upload': <Presentation className="h-4 w-4" />,
      'pitch_submission': <FileText className="h-4 w-4" />,
      'demo_guidelines': <FileText className="h-4 w-4" />,
      'brand_promotion': <MessageSquare className="h-4 w-4" />,
      'device_prep': <Settings className="h-4 w-4" />,
      'promotional_flyer': <FileText className="h-4 w-4" />,
      'event_day': <Calendar className="h-4 w-4" />,
      'fo_nominations': <Star className="h-4 w-4" />,
      default: <FileText className="h-4 w-4" />
    };
    return icons[taskType] || icons.default;
  };

  const renderTaskData = (task) => {
    if (!task.data) return null;

    switch (task.taskType) {
      case 'company_details':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Company Information</h4>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-500">Company:</span> <span className="font-medium">{task.data.companyName}</span></div>
                <div><span className="text-gray-500">POC:</span> <span className="font-medium">{task.data.pocName}</span></div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium">{task.data.pocEmail}</span>
                  <Copy className="h-3 w-3 cursor-pointer text-gray-400 hover:text-gray-600" onClick={() => copyToClipboard(task.data.pocEmail)} />
                </div>
                <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{task.data.pocPhone}</span></div>
                {task.data.website && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Website:</span>
                    <a href={task.data.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                      {task.data.website}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Presenter Information</h4>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium">{task.data.presenterName || 'N/A'}</span></div>
                <div><span className="text-gray-500">Title:</span> <span className="font-medium">{task.data.presenterTitle || 'N/A'}</span></div>
                <div><span className="text-gray-500">Email:</span> <span className="font-medium">{task.data.presenterEmail || 'N/A'}</span></div>
                <div><span className="text-gray-500">Mobile:</span> <span className="font-medium">{task.data.presenterMobile || 'N/A'}</span></div>
              </div>
            </div>

            {task.data.description && (
              <div className="col-span-2 space-y-2">
                <h4 className="font-semibold text-sm">Company Description</h4>
                <p className="text-sm text-gray-600">{task.data.description}</p>
              </div>
            )}

            {/* Company Logos Section */}
            {(task.data.logos && task.data.logos.length > 0) || task.data.logoPath ? (
              <div className="col-span-2 space-y-3">
                <h4 className="font-semibold text-sm">Company Logos</h4>
                {task.data.logos && task.data.logos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {task.data.logos.map((logo, index) => (
                      <div key={index} className="border rounded-lg p-3 bg-white">
                        <img
                          src={logo.url}
                          alt={`Logo ${index + 1}`}
                          className="w-full h-20 object-contain mb-2 cursor-pointer hover:opacity-80"
                          onClick={() => window.open(logo.url, '_blank')}
                        />
                        <p className="text-xs font-medium text-center mb-1">
                          Logo {index + 1}
                        </p>
                        {logo.size && (
                          <p className="text-xs text-gray-500 mb-2">
                            {(logo.size / 1024).toFixed(1)} KB
                          </p>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(logo.url, `${task.data.companyName}_logo_${index + 1}.png`)}
                          className="w-full text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : task.data.logoPath ? (
                  // Fallback to single logo if logos array is empty
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(task.data.logoPath, `${task.data.companyName}_logo.png`)}
                      className="flex items-center gap-2"
                    >
                      <Image className="h-4 w-4" />
                      Download Logo
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Presenter Photo Section */}
            {task.data.presenterPhotoPath && (
              <div className="col-span-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(task.data.presenterPhotoPath, `${task.data.presenterName}_photo.jpg`)}
                  className="flex items-center gap-2"
                >
                  <Image className="h-4 w-4" />
                  Download Presenter Photo
                </Button>
              </div>
            )}
          </div>
        );

      case 'networking_passes':
        return (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            {task.data.goldPasses && task.data.goldPasses.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Gold Passes ({task.data.goldPasses.length})</h4>
                <div className="space-y-2">
                  {task.data.goldPasses.map((pass, index) => (
                    <div key={index} className="bg-yellow-50 p-3 rounded border border-yellow-200">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Name:</span> <span className="font-medium">{pass.firstName} {pass.lastName}</span></div>
                        <div><span className="text-gray-500">Designation:</span> <span className="font-medium">{pass.designation}</span></div>
                        <div><span className="text-gray-500">Email:</span> <span className="font-medium">{pass.email}</span></div>
                        <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{pass.phone?.countryCode} {pass.phone?.number}</span></div>
                        {pass.passCode && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Pass Code:</span>
                            <span className="font-mono font-bold ml-2">{pass.passCode}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {task.data.silverPlusPasses && task.data.silverPlusPasses.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Silver Plus Passes ({task.data.silverPlusPasses.length})</h4>
                <div className="space-y-2">
                  {task.data.silverPlusPasses.map((pass, index) => (
                    <div key={index} className="bg-purple-50 p-3 rounded border border-purple-200">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Name:</span> <span className="font-medium">{pass.firstName} {pass.lastName}</span></div>
                        <div><span className="text-gray-500">Designation:</span> <span className="font-medium">{pass.designation}</span></div>
                        <div><span className="text-gray-500">Email:</span> <span className="font-medium">{pass.email}</span></div>
                        <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{pass.phone?.countryCode} {pass.phone?.number}</span></div>
                        {pass.passCode && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Pass Code:</span>
                            <span className="font-mono font-bold ml-2">{pass.passCode}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {task.data.silverPasses && task.data.silverPasses.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Silver Passes ({task.data.silverPasses.length})</h4>
                <div className="space-y-2">
                  {task.data.silverPasses.map((pass, index) => (
                    <div key={index} className="bg-gray-100 p-3 rounded border border-gray-300">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Name:</span> <span className="font-medium">{pass.firstName} {pass.lastName}</span></div>
                        <div><span className="text-gray-500">Designation:</span> <span className="font-medium">{pass.designation}</span></div>
                        <div><span className="text-gray-500">Email:</span> <span className="font-medium">{pass.email}</span></div>
                        <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{pass.phone?.countryCode} {pass.phone?.number}</span></div>
                        {pass.passCode && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Pass Code:</span>
                            <span className="font-mono font-bold ml-2">{pass.passCode}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-sm text-gray-600">
              Total Allocated: {task.data.totalAllocated} passes
            </div>
          </div>
        );

      case 'demo_guidelines':
        return (
          <div className="p-4 bg-gray-50 rounded-lg text-sm">
            <div className="space-y-2">
              <div><span className="text-gray-500">Read at:</span> <span className="font-medium">{formatDate(task.data.readAt)}</span></div>
              <div><span className="text-gray-500">Read by:</span> <span className="font-medium">{task.data.readBy}</span></div>
            </div>
          </div>
        );

      case 'pitch_submission':
        return (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            {/* Presenter Information Section */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Presenter Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-white rounded border">
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="font-medium ml-2">
                      {task.data.presenterName ||
                       (task.data.presenterFirstName && task.data.presenterLastName ?
                        `${task.data.presenterFirstName} ${task.data.presenterLastName}` :
                        'N/A')}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Title:</span>
                    <span className="font-medium ml-2">{task.data.presenterTitle || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Company:</span>
                    <span className="font-medium ml-2">{task.data.presenterCompanyName || 'N/A'}</span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">Email:</span>
                    <span className="font-medium">{task.data.presenterEmail || 'N/A'}</span>
                    {task.data.presenterEmail && (
                      <Copy
                        className="h-3 w-3 cursor-pointer text-gray-400 hover:text-gray-600"
                        onClick={() => copyToClipboard(task.data.presenterEmail)}
                      />
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500">Mobile:</span>
                    <span className="font-medium ml-2">
                      {task.data.presenterMobile ?
                        `${task.data.presenterCountryCode || '+91'} ${task.data.presenterMobile}` :
                        'N/A'}
                    </span>
                  </div>
                  {task.data.presenterLinkedIn && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">LinkedIn:</span>
                      <span className="font-medium">{task.data.presenterLinkedIn}</span>
                      <Copy
                        className="h-3 w-3 cursor-pointer text-gray-400 hover:text-gray-600"
                        onClick={() => copyToClipboard(task.data.presenterLinkedIn)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {task.data.teamBackground && (
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Team Background</h4>
                <p className="text-gray-700">{task.data.teamBackground}</p>
              </div>
            )}

            {task.data.useOfFunds && (
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Use of Funds</h4>
                <p className="text-gray-700">{task.data.useOfFunds}</p>
              </div>
            )}

            {task.data.additionalNotes && (
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Additional Notes</h4>
                <p className="text-gray-700">{task.data.additionalNotes}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {task.data.documentUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(task.data.documentUrl, '_blank')}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  View Pitch Deck
                </Button>
              )}
              {task.data.videoUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(task.data.videoUrl, '_blank')}
                  className="flex items-center gap-2"
                >
                  <Video className="h-4 w-4" />
                  View Video
                </Button>
              )}
              {task.data.presenterPhotoPath && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(task.data.presenterPhotoPath, `${task.data.presenterName || 'presenter'}_photo.jpg`)}
                  className="flex items-center gap-2"
                >
                  <Image className="h-4 w-4" />
                  Download Presenter Photo
                </Button>
              )}
            </div>
          </div>
        );

      case 'brand_promotion':
        return (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            {task.data.socialMediaProfiles && task.data.socialMediaProfiles.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">Social Media Profiles</h4>
                <div className="space-y-2">
                  {task.data.socialMediaProfiles.map((profile, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <a href={profile.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                        {profile.platform}: {profile.handle || profile.url}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {task.data.promotionalContent && (
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Promotional Content</h4>
                <p className="text-gray-700">{task.data.promotionalContent}</p>
              </div>
            )}

            {task.data.additionalNotes && (
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Additional Notes</h4>
                <p className="text-gray-700">{task.data.additionalNotes}</p>
              </div>
            )}
          </div>
        );

      case 'device_prep':
        return (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-sm mb-3">Technical Checklist</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress:</span>
                <span className="font-medium">{task.data.completedItems} / {task.data.totalItems} items completed</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(task.data.completedItems / task.data.totalItems) * 100}%` }}
                />
              </div>
              {task.data.checklist && task.data.checklist.length > 0 && (
                <div className="mt-3 space-y-1">
                  {task.data.checklist.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      {item.completed ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={item.completed ? 'line-through text-gray-500' : ''}>{item.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {task.data.completedAt && (
                <div className="text-sm text-gray-600 mt-2">
                  Completed on: {formatDate(task.data.completedAt)}
                </div>
              )}
            </div>
          </div>
        );

      case 'promotional_flyer':
        return (
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <div className="text-sm space-y-2">
              <div><span className="text-gray-500">Status:</span> <span className="font-medium">{task.data.enabled ? 'Enabled' : 'Disabled'}</span></div>
              {task.data.enabledAt && (
                <div><span className="text-gray-500">Enabled at:</span> <span className="font-medium">{formatDate(task.data.enabledAt)}</span></div>
              )}
            </div>

            {task.data.shippingAddress && (
              <div className="text-sm">
                <h4 className="font-semibold mb-1">Shipping Address</h4>
                <div className="bg-white p-2 rounded border">
                  <div>{task.data.shippingAddress.company}</div>
                  <div>{task.data.shippingAddress.address}</div>
                  <div>{task.data.shippingAddress.city}</div>
                  <div>{task.data.shippingAddress.state}</div>
                </div>
              </div>
            )}

            {task.data.flyerUrl && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDownload(task.data.flyerUrl, 'promotional_flyer.pdf')}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Flyer
              </Button>
            )}
          </div>
        );

      case 'fo_nominations':
        return (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            {task.data?.nominees && task.data.nominees.length > 0 ? (
              <div>
                <h4 className="font-semibold text-sm mb-2">Nominees ({task.data.totalNominees})</h4>
                <div className="space-y-2">
                  {task.data.nominees.map((nominee, index) => (
                    <div key={index} className="bg-white p-3 rounded border">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Name:</span> <span className="font-medium">{nominee.name}</span></div>
                        <div><span className="text-gray-500">Designation:</span> <span className="font-medium">{nominee.designation}</span></div>
                        <div className="col-span-2"><span className="text-gray-500">Company:</span> <span className="font-medium">{nominee.companyName}</span></div>
                        {nominee.email && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Email:</span>
                            <span className="font-medium">{nominee.email}</span>
                            <Copy
                              className="h-3 w-3 cursor-pointer text-gray-400 hover:text-gray-600"
                              onClick={() => copyToClipboard(nominee.email)}
                            />
                          </div>
                        )}
                        {nominee.linkedin && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">LinkedIn:</span>
                            <a href={nominee.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                              View Profile
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {task.data.submittedAt && (
                  <div className="text-sm text-gray-600 mt-2">
                    Submitted on: {formatDate(task.data.submittedAt)}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No nominees submitted yet</div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {company.companyName}
          </DialogTitle>
          <DialogDescription>
            View and manage tasks for this company
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full"></div>
          </div>
        ) : taskDetails ? (
          <div className="space-y-6">
            {/* Company Info Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Company Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">POC:</span>
                    <span className="font-medium">{company.pocName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{company.pocEmail}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-medium">{company.pocPhone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Registered:</span>
                    <span className="font-medium">{formatDate(company.registeredAt)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Admin Controls */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Admin Controls</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {company?.networkingPassesLocked ? (
                        <Lock className="h-4 w-4 text-red-500" />
                      ) : (
                        <Unlock className="h-4 w-4 text-green-500" />
                      )}
                      <div>
                        <span className="text-sm font-medium">Network Passes</span>
                        <p className="text-xs text-gray-500">
                          {company?.networkingPassesLocked
                            ? 'Locked - Companies cannot allocate passes'
                            : 'Unlocked - Companies can allocate passes'}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={company?.networkingPassesLocked ? 'destructive' : 'default'}
                      onClick={toggleNetworkPassesLock}
                      disabled={togglingLock}
                    >
                      {togglingLock ? (
                        <span className="flex items-center gap-2">
                          <Clock className="h-3 w-3 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {company?.networkingPassesLocked ? (
                            <><Unlock className="h-3 w-3" /> Unlock</>
                          ) : (
                            <><Lock className="h-3 w-3" /> Lock</>
                          )}
                        </span>
                      )}
                    </Button>
                  </div>
                  {company?.networkingPassesLockToggled && (
                    <p className="text-xs text-gray-500">
                      Last toggled: {new Date(company.networkingPassesLockToggled).toLocaleString()}
                      {company?.networkingPassesLockToggledBy && ` by ${company.networkingPassesLockToggledBy}`}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Progress Overview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Overall Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Completion</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {company.progressPercentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${company.progressPercentage}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{company.completedTasks} completed</span>
                    <span>{company.totalTasks - company.completedTasks} remaining</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tasks List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Task Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {taskDetails.tasks.map((task) => (
                    <div
                      key={task._id}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div
                        className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => toggleTaskExpansion(task._id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="mt-0.5">
                              {expandedTasks.has(task._id) ? (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                            {getStatusIcon(task.status)}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {getTaskIcon(task.taskType)}
                                <h4 className="font-medium">{task.taskName}</h4>
                                {getStatusBadge(task.status)}
                                {task.isOptional && (
                                  <Badge variant="outline" className="text-xs">Optional</Badge>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                              )}
                              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                                <span>Due: {formatDate(task.dueDate)}</span>
                                {task.completedAt && (
                                  <span>Completed: {formatDate(task.completedAt)}</span>
                                )}
                              </div>
                              {task.adminNotes && (
                                <div className="mt-2 p-2 bg-yellow-50 rounded text-sm">
                                  <span className="font-medium">Admin Notes:</span> {task.adminNotes}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            {editingTask === task._id ? (
                              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                                <select
                                  className="px-3 py-1 border rounded text-sm"
                                  defaultValue={task.status}
                                  onChange={(e) => {
                                    updateTaskStatus(task._id, e.target.value);
                                  }}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                  <option value="blocked">Blocked</option>
                                </select>
                                <Textarea
                                  placeholder="Add admin notes..."
                                  value={adminNotes}
                                  onChange={(e) => setAdminNotes(e.target.value)}
                                  className="text-sm"
                                  rows={2}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingTask(null);
                                      setAdminNotes('');
                                    }}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTask(task._id);
                                  setAdminNotes(task.adminNotes || '');
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {expandedTasks.has(task._id) && (
                        <div className="border-t">
                          {task.data ? (
                            renderTaskData(task)
                          ) : (
                            <div className="p-4 text-sm text-gray-500">
                              No data submitted yet
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No task details available
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}